import React, { useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { i18nIntl, isRemoteDb } from '../../lib/ep';

import ActionButton, { SecondaryButton } from '../builder/ActionButton';
import ButtonCollection from '../builder/ButtonCollection';
import FloatingLayer from '../builder/FloatingLayer';
import SectionSubtitle from '../builder/SectionSubtitle';
import SectionTitle from '../builder/SectionTitle';
import SaveButton from '../builder/SaveButton';
import { ThesaurusLink } from '../builder/ActionLink';
import Tooltip from '../builder/Tooltip';

const BlockLayerWrapper = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	return (
		<FloatingLayer
			backButton={props.close}
			_class={{ size:'small' }}
		>
			<div>
				<SectionTitle _class={{ other:'knk-alert redIcon' }}>Warning</SectionTitle>

				<SectionSubtitle
					autotranslate={false}
				><FormattedMessage
					id="Do you want to block the {type} {item}?"
					values={{
						type:props.type,
						item:<strong>{props.item}</strong>,
					}}
				/></SectionSubtitle>

				<div class="gui_not_in_top">
					<p>{i18n("If you do")}:</p>

					<ol class="bulleted_list gui_padded_children_v not_in_top">
						{props.actions_to_do.map((action, a) => {
							return (
								<li key={`action_${a}`}><strong>{action}</strong></li>
							)
						})}
					</ol>
				</div>

				<ButtonCollection in_floating={true}>
					<SaveButton
						onClick={props.success}
					>{props?.success_btn_label ?? "Yes, block"}</SaveButton>

					<SecondaryButton
						onClick={props.close}
					>{props?.cancel_btn_label ?? "No, cancel"}</SecondaryButton>
				</ButtonCollection>
			</div>
		</FloatingLayer>
	)
};

const UnblockLayerWrapper = (props) => {
	return (
		<FloatingLayer
			backButton={props.close}
			_class={{ size:'small' }}
		>
			<div>
				<SectionTitle _class={{ other:'knk-alert redIcon' }}>Warning</SectionTitle>

				<SectionSubtitle
					autotranslate={false}
				><FormattedMessage
					id="If you unblock the {type} {item}, the already blocked users will remain as blocked."
					values={{
						type:props.type,
						item:<strong>{props.item}</strong>,
					}}
				/></SectionSubtitle>

				<ButtonCollection in_floating={true}>
					<SaveButton
						onClick={(e,cb)=>props.success(cb)}
					>{props?.success_btn_label ?? "Yes, unblock"}</SaveButton>

					<SecondaryButton
						onClick={props.close}
					>{props?.cancel_btn_label ?? "No, keep it blocked"}</SecondaryButton>
				</ButtonCollection>
			</div>
		</FloatingLayer>
	)
};

const BlockItem = ({ item, ...props }) => {
	const [ display_layer, setDisplayLayer ] = useState(false);
	const [ is_blocked, setIsBlocked ] = useState(props?.is_blocked ?? false);

	if(item == undefined) return null;

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const enable_save = (window.server_type != '1' && !isRemoteDb()) ? true : false;

	const type = props?.type ?? 'ip';
	const button_props = props?.button_props ?? {};

	const toggle_block = (is_block, cb) => {
		if(!enable_save) {
			alert("Block functionality is disabled in non-production environment to avoid accidental real action.");
			if(props.onBlock != undefined) props.onBlock(item);
			
			if(cb != undefined) cb();
			
			setTimeout(() => {
				setIsBlocked(is_block);
				setDisplayLayer(false);
			}, 500);

			return;
		}

		//OJO no posar amb remoteDB!
		const url = `/promotions/${type == 'ip' ? 'toggle_block_ip' : 'toggle_block_domain'}/${props.promotion_id}`;
		$.post(url, { [type]:item, type, is_block }, (result) => {
			if(result.status) {
				if(props.onBlock != undefined && is_block) props.onBlock(item);
				if(props.onUnblock != undefined && !is_block) props.onUnblock(item);
				
				setTimeout(() => {
					setIsBlocked(is_block);
					setDisplayLayer(false);
				}, 500);
			} else if(result.reason) {
				console.error(result.reason);
			}

			if(cb != undefined) cb();
		}, 'JSON');
	};

	const block_layer_props = {
		close:() => setDisplayLayer(false),
		success:(e, cb) => toggle_block(true, cb),
		item,
		intl:props.intl,
	};

	const unblock_layer_props = {
		close:() => setDisplayLayer(false),
		success:(e, cb) => toggle_block(false, cb),
		item,
	};

	let type_label = i18n("IP");
	switch(type) {
		case 'domain':
			type_label = i18n("domain");
			break;

		case 'ip':
		default:
			type_label = i18n("ip", 'uppercase');
			break;
	}

	const block_them_tooltip = <ThesaurusLink
		tooltip={<Tooltip with_icon={false} position='top'>A blocked user will not be able to participate further in the promotion and will not be eligible for any new prizes. They will appear with a red mark in your user list, and all their participations will be unpublished and marked as suspicious.</Tooltip>}
	>block all users</ThesaurusLink>;

	const not_allowed_domains = <ThesaurusLink
		tooltip={<Tooltip with_icon={false} position='top'>New users attempting to register for this promotion with this domain will be blocked.</Tooltip>}
	>not-allowed emails</ThesaurusLink>;

	return (
		<>
			<ActionButton
				children={props?.children ?? "Block item"}
				icon={`knk-blacklist${is_blocked ? ' mainButton whiteIcon' : ''}`}
				{...button_props}
				onClick={() => setDisplayLayer(true)}
			/>

			{(!is_blocked && display_layer && type == 'ip') && (
				<BlockLayerWrapper
					{...block_layer_props}
					type={type_label}
					actions_to_do={[
						<FormattedMessage id="You will {block_them} who registered through this {type}." values={{ block_them:block_them_tooltip, type:type_label }} />,
						props.intl.formatMessage({ id:"You will add the IP address to your blacklist. No one else will be able to register and participate with this IP in your promotions." }, { type:type_label }),
					]}
					success_btn_label="Yes, block IP"
				/>
			)}

			{(!is_blocked && display_layer && type == 'domain') && (
				<BlockLayerWrapper
					{...block_layer_props}
					type={type_label}
					actions_to_do={[
						<FormattedMessage id="You will {block_them} who registered through this {type}." values={{ block_them:block_them_tooltip, type:type_label }} />,
						<FormattedMessage id="You will add this domain to your {not_allowed_domains} for this promotion." values={{ not_allowed_domains:not_allowed_domains }} />,
						props.intl.formatMessage({ id:"You will send an automatic alert to Easypromos Security Team to analize this {type}." }, { type:type_label }),
					]}
					success_btn_label="Yes, block domain"
				/>
			)}

			{(is_blocked && display_layer && type == 'ip') && (
				<UnblockLayerWrapper
					{...unblock_layer_props}
					type={type_label}
					success_btn_label="Yes, unblock IP"
				/>
			)}

			{(is_blocked && display_layer && type == 'domain') && (
				<UnblockLayerWrapper
					{...unblock_layer_props}
					type={type_label}
					success_btn_label="Yes, unblock domain"
				/>
			)}
		</>
	)
};

export default injectIntl(BlockItem);